/**
 * Client-side chat-thread auto-naming, mirroring the backend `auto_name_from`.
 *
 * The backend auto-names a thread from its first user message and persists it
 * (`services/chat/threads.py`: `auto_name_from` / `_maybe_auto_name`), so a
 * *reloaded* thread shows the derived title. The live in-session tab, however,
 * only learns the server thread id from the SSE turn (never the title), so the
 * tab would otherwise stay on its default placeholder until reload. This helper
 * derives the same title on the client so the live tab matches the persisted
 * one with no drift — same whitespace-collapse + 60-char truncation rule.
 */

/** Max length of an auto-generated title before truncation (backend parity). */
const AUTO_NAME_MAX_LEN = 60;

/**
 * The default placeholder title for a fresh, unsent chat tab.
 *
 * Shared by {@link deriveThreadTitle} (its empty-input fallback) and the tab
 * factory so the auto-namer's "still default" guard has a single source of
 * truth.
 */
export const DEFAULT_THREAD_TITLE = "New chat";

/**
 * Derive a concise, single-line thread title from a user message.
 *
 * Collapses runs of whitespace to single spaces and truncates to
 * {@link AUTO_NAME_MAX_LEN} characters (last char becomes an ellipsis), matching
 * the backend `auto_name_from`. Falls back to {@link DEFAULT_THREAD_TITLE} for
 * empty/whitespace-only input (the Composer already blocks empty sends).
 *
 * @param text - The first user message content.
 * @returns The derived title, or the default placeholder for blank input.
 */
export function deriveThreadTitle(text: string): string {
  const summary = text.split(/\s+/).filter(Boolean).join(" ");
  if (summary === "") return DEFAULT_THREAD_TITLE;
  const chars = Array.from(summary);
  if (chars.length <= AUTO_NAME_MAX_LEN) return summary;
  return (
    chars
      .slice(0, AUTO_NAME_MAX_LEN - 1)
      .join("")
      .trimEnd() + "…"
  );
}
